import { type ModelControls, STORED_NONE } from "./model-controls";
import {
  ALL_DIALECT_EFFORT_LEVELS,
  type AnyDialectEffortLevel,
  type DefaultEffort,
  type EffortRung,
  rungOf
} from "./thinking-dialect.registry";

export type EffortChoice = DefaultEffort;

export interface TuningChoice {
  readonly effort: EffortChoice;
  readonly writes: string | undefined;
}

export const TUNING_HINTS = ["always-thinks", "temperature-when-none"] as const;
export type TuningHint = (typeof TUNING_HINTS)[number];

export interface TuningChoicesInput {
  readonly controls: ModelControls;
  readonly storedWord: string | undefined;
}

export interface TuningChoices {
  readonly choices: readonly TuningChoice[];
  readonly selected: EffortChoice | undefined;
  readonly keeps: string | undefined;
  readonly showsTemperature: boolean;
  readonly showsDisplay: boolean;
  readonly hints: readonly TuningHint[];
}

type ControlLevel = ModelControls["levels"][number];

const isEffortLevel = (word: string): word is AnyDialectEffortLevel =>
  (ALL_DIALECT_EFFORT_LEVELS as readonly string[]).includes(word);

const isLevelChoice = (choice: EffortChoice | undefined): choice is EffortRung =>
  choice !== undefined && choice !== STORED_NONE && choice !== "vendor-default";

function levelFor(levels: ModelControls["levels"], word: string): ControlLevel | undefined {
  const own = levels.find((level) => level.stored === word);
  if (own !== undefined || !isEffortLevel(word)) return own;
  const mappedRung = rungOf(word);
  return levels.find((level) => level.stored === mappedRung);
}

interface StoredReading {
  readonly selected: EffortChoice;
  readonly keeps: string | undefined;
}

function readStoredWord(controls: ModelControls, word: string | undefined): StoredReading {
  const unset: StoredReading = { selected: controls.effortWhenUnset, keeps: undefined };
  if (word === undefined) return unset;
  if (word === STORED_NONE) {
    return controls.offersNone ? { selected: STORED_NONE, keeps: STORED_NONE } : unset;
  }
  const level = levelFor(controls.levels, word);
  return level === undefined ? unset : { selected: level.rung, keeps: level.stored };
}

export function tuningChoicesOf({ controls, storedWord }: TuningChoicesInput): TuningChoices {
  const word = storedWord?.toLowerCase();
  const hasLevels = controls.levels.length > 0;
  const hasAnyChoice = hasLevels || controls.offersNone;

  const offersVendorDefault = hasAnyChoice && controls.effortWhenUnset === "vendor-default";
  const offersNoneChoice = controls.offersNone;

  const vendorDefaultChoice: TuningChoice = { effort: "vendor-default", writes: undefined };
  const noneChoice: TuningChoice = { effort: STORED_NONE, writes: STORED_NONE };
  const levelChoices: readonly TuningChoice[] = controls.levels.map((level) => ({
    effort: level.rung,
    writes: level.stored
  }));

  const choices: readonly TuningChoice[] = [
    ...(offersVendorDefault ? [vendorDefaultChoice] : []),
    ...(offersNoneChoice ? [noneChoice] : []),
    ...levelChoices
  ];

  const { selected, keeps } = hasAnyChoice
    ? readStoredWord(controls, word)
    : { selected: undefined, keeps: undefined };
  const isLevelSelected = isLevelChoice(selected);

  return {
    choices,
    selected,
    keeps,
    showsTemperature:
      controls.temperature === "always" ||
      (controls.temperature === "when-off" && !isLevelSelected),
    showsDisplay: controls.offersDisplay && isLevelSelected,
    hints: [
      ...(hasLevels && !controls.offersNone && controls.offFormDeclared
        ? (["always-thinks"] as const)
        : []),
      ...(controls.temperature === "when-off" && isLevelSelected && offersNoneChoice
        ? (["temperature-when-none"] as const)
        : [])
    ]
  };
}

export interface CarriedEffortInput {
  readonly from: ModelControls | undefined;
  readonly storedWord: string | undefined;
  readonly to: ModelControls;
}

interface ChosenEffort {
  readonly effort: EffortChoice;
  readonly word: string;
}

interface ChosenEffortInput {
  readonly from: ModelControls | undefined;
  readonly word: string;
}

function chosenEffortOf({ from, word }: ChosenEffortInput): ChosenEffort | undefined {
  if (from !== undefined) {
    const { selected, keeps } = readStoredWord(from, word);
    return keeps === undefined ? undefined : { effort: selected, word: keeps };
  }
  if (word === STORED_NONE) return { effort: STORED_NONE, word };
  return isEffortLevel(word) ? { effort: rungOf(word), word } : undefined;
}

export function carriedEffortLevel({
  from,
  storedWord,
  to
}: CarriedEffortInput): string | undefined {
  if (storedWord === undefined) return undefined;
  const chosen = chosenEffortOf({ from, word: storedWord.toLowerCase() });
  if (chosen === undefined) return undefined;
  const candidates = tuningChoicesOf({ controls: to, storedWord: undefined }).choices.filter(
    (choice) => choice.effort === chosen.effort && choice.writes !== undefined
  );
  const carried =
    candidates.find((choice) => choice.writes === chosen.word) ??
    candidates.find((choice) => choice.writes === chosen.effort) ??
    candidates[0];
  return carried?.writes;
}
